export const sampleBookMeta = {
  title: "The Unsolicited Opinions",
  subtitle: "A Group Chat Memoir",
  year: 2024,
  yearbookType: "group",
  dedication: "To the group chat that has 847 unread messages and zero plans that ever actually happened — may this book be the only thing we ever follow through on.",
};

export const sampleCharacters = [
  {
    name: "Jake",
    bio: "Self-appointed historian of the group chat. Will screenshot a message from 2019 and bring it up at the worst possible time. Has a photographic memory for everyone's contradictions and absolutely zero chill about it. Once pinned a message for 'legal purposes.'",
    traits: ["Group Historian", "Screenshot Archivist", "Receipts Collector"],
    signature_quote: "Actually, on March 14th 2021 you said the opposite",
    story_count: 15,
  },
  {
    name: "Mia",
    bio: "Creates polls that nobody votes on, sends Google Docs for weekend plans, and is one missed RSVP away from a full breakdown. Her energy is project manager for a group that never agreed to be managed. Passive-aggressive use of the 'noted.' is her signature move.",
    traits: ["Planner-in-Chief", "Poll Creator", "Passive-Aggressive RSVP Enforcer"],
    signature_quote: "So I guess nobody's voting on the poll then. Cool.",
    story_count: 15,
  },
  {
    name: "Tyler",
    bio: "Goes completely silent for three weeks and then drops a take so unhinged the group chat doesn't recover for days. Has never once said 'good morning' or engaged in small talk. Only appears to start fires and then watches from the shadows.",
    traits: ["Chaos Agent", "Hot Take Specialist", "Three-Week Lurker"],
    signature_quote: "Hear me out",
    story_count: 8,
  },
  {
    name: "Priya",
    bio: "Reacts to every single message but has never once read a full thread. Will respond 'LMAOOO' to something from six hours ago and then ask 'wait what happened?' Her scroll-to-react ratio is legendary. Once hearted a breakup announcement thinking it was a joke.",
    traits: ["Reaction Queen", "Selective Reader", "Perpetually Confused"],
    signature_quote: "wait can someone summarize I'm not reading all that",
    story_count: 15,
  },
  {
    name: "Nate",
    bio: "Refuses to type. Every response is a voice note ranging from 45 seconds to 4 minutes. Has sent a voice note to answer a yes-or-no question. The group has collectively listened to approximately 73 hours of Nate's monologues, most of which could have been three words.",
    traits: ["Voice Note Only", "Monologue King", "Anti-Typing Activist"],
    signature_quote: "[Voice Note - 3:47]",
    story_count: 15,
  },
];

export const sampleChapters = [
  { index: 0, title: "The Great Group Name Debate", title_emoji: "\u{1F4AC}", date: "Jan 5, 2024", is_sample: true },
  { index: 1, title: "Nobody RSVPs", title_emoji: "\u{1F4CB}", date: "Jan 20, 2024", is_sample: false },
  { index: 2, title: "Tyler's 3AM Take", title_emoji: "\u{1F525}", date: "Feb 8, 2024", is_sample: false },
  { index: 3, title: "The Restaurant Debate", title_emoji: "\u{1F37D}\u{FE0F}", date: "Feb 22, 2024", is_sample: true },
  { index: 4, title: "Fantasy League Meltdown", title_emoji: "\u{1F3C8}", date: "Mar 10, 2024", is_sample: false },
  { index: 5, title: "Nate's Longest Voice Note", title_emoji: "\u{1F3A4}", date: "Mar 28, 2024", is_sample: false },
  { index: 6, title: "Trip Planning (Day 47)", title_emoji: "\u{2708}\u{FE0F}", date: "Apr 14, 2024", is_sample: false },
  { index: 7, title: "The Gym Phase", title_emoji: "\u{1F4AA}", date: "May 1, 2024", is_sample: false },
  { index: 8, title: "Movie Night Negotiations", title_emoji: "\u{1F3AC}", date: "May 19, 2024", is_sample: false },
  { index: 9, title: "Jake's Receipts Folder", title_emoji: "\u{1F4F8}", date: "Jun 7, 2024", is_sample: false },
  { index: 10, title: "The Birthday Fund Disaster", title_emoji: "\u{1F381}", date: "Jun 25, 2024", is_sample: false },
  { index: 11, title: "Priya Discovers the Thread", title_emoji: "\u{1F440}", date: "Jul 15, 2024", is_sample: false },
  { index: 12, title: "The 2AM Philosophy Session", title_emoji: "\u{1F30C}", date: "Aug 20, 2024", is_sample: true },
  { index: 13, title: "Fantasy Draft Day Chaos", title_emoji: "\u{1F3C6}", date: "Sep 5, 2024", is_sample: false },
  { index: 14, title: "New Year's Plan (Attempt #4)", title_emoji: "\u{1F389}", date: "Oct 18, 2024", is_sample: false },
];

export const sampleStories = [
  {
    story_id: "sample-group-001",
    chapter_index: 0,
    title: "The Great Group Name Debate",
    title_emoji: "\u{1F4AC}",
    date: "January 5, 2024",
    time_range: "4:12 PM - 1:30 AM",
    participants: ["Jake", "Mia", "Tyler", "Priya", "Nate"],
    tags: ["Democracy", "Chaos", "Still Contested"],
    narrative: `It began, as most catastrophes do, with [[Mia|Mia]] trying to organize something.

"Okay, new year new us. We need a real group chat name. I'm making a poll."

She posted a Google Form — an actual Google Form, with a submissions deadline and everything — containing five options she had pre-selected: "The Crew," "The Inner Circle," "Five Alive," "The Brain Trust," and "Other (please specify)."

Nobody filled out the form.

"The form is anonymous," she added. "There's no reason not to vote."

"I'm not filling out a form to name a group chat," [[Jake|Jake]] replied. "We're not a startup."

[[Nate|Nate]] sent a voice note. It was two minutes and fourteen seconds long. The summary, which [[Priya|Priya]] demanded because she "wasn't listening to all that," was that Nate thought the name should be "The Boys" even though Mia and Priya were in the group.

"Nate, there are women here," [[Mia|Mia]] typed.

[[Nate|Nate]] sent another voice note: fifty-three seconds explaining that "boys" is gender-neutral in a group chat context. Nobody agreed.

[[Jake|Jake]] suggested "The Unsolicited Opinions" and immediately followed with "because that's literally all we do here."

"I actually love that," [[Priya|Priya]] replied, adding seven laughing emojis.

"It's kind of long," [[Mia|Mia]] noted.

"So are Nate's voice notes and we tolerate those," [[Jake|Jake]] responded.

At 8:47 PM, [[Tyler|Tyler]] appeared. He hadn't messaged in eleven days. His contribution was a single message: "The Council."

"Tyler emerges from the shadows," [[Jake|Jake]] narrated.

"The Council sounds like we're deciding someone's fate," [[Priya|Priya]] said.

"Are we not?" [[Tyler|Tyler]] replied. Then he went silent again.

The debate raged for hours. [[Mia|Mia]] updated her Google Form three times. [[Nate|Nate]] sent four more voice notes, each longer than the last, lobbying for "The Boys" with increasingly elaborate arguments. [[Priya|Priya]] changed the group name to "JUST PICK ONE" out of frustration, which everyone agreed was actually pretty good but couldn't be the permanent name because it "lacked gravitas," according to Jake.

At 12:45 AM, [[Mia|Mia]] called a vote directly in the chat. "The Unsolicited Opinions" won 3-2. [[Nate|Nate]] and [[Tyler|Tyler]] voted against. Nate sent a voice note concession speech that was three minutes and forty-seven seconds long.

[[Mia|Mia]] changed the group name at 1:15 AM. "It's done. No more changes."

At 1:30 AM, [[Tyler|Tyler]] sent: "I'm appealing this." He did not elaborate. The name stuck.`,
  },
  {
    story_id: "sample-group-003",
    chapter_index: 3,
    title: "The Restaurant Debate",
    title_emoji: "\u{1F37D}\u{FE0F}",
    date: "February 22, 2024",
    time_range: "5:30 PM - 9:15 PM",
    participants: ["Jake", "Mia", "Tyler", "Priya", "Nate"],
    tags: ["Dinner Plans", "Indecision", "Hunger"],
    narrative: `[[Mia|Mia]] posted at 5:30 PM: "Dinner tonight? I made a poll." She included a link to — incredibly — another Google Form, this one with twelve restaurant options, dietary restriction checkboxes, a budget slider, and a field for "parking preferences."

"Mia, we're picking a restaurant, not buying a house," [[Jake|Jake]] replied.

"Last time we didn't plan, we walked around for forty minutes and ended up at a gas station," [[Mia|Mia]] shot back.

"That gas station had great hot dogs," [[Nate|Nate]] sent via voice note. Seventeen seconds. Brief, for him.

[[Priya|Priya]] reacted to the poll link with a heart emoji, which Mia took as a vote. It was not a vote. Priya had not opened the link.

"Priya, did you actually vote?" [[Mia|Mia]] asked.

"I hearted it, that's basically voting."

"That is not how democracy works."

[[Jake|Jake]] suggested the Thai place on 5th. [[Nate|Nate]] countered with a one-minute-forty-two-second voice note explaining that the Thai place gave him heartburn last time and providing a detailed account of the aftermath that nobody had asked for.

"Nate, I didn't need to know any of that," [[Mia|Mia]] replied.

"I'm just being transparent with the group."

[[Tyler|Tyler]] appeared at 7:01 PM — ninety minutes into the debate — with: "Hear me out. Waffle House."

"It is a Thursday," [[Jake|Jake]] pointed out.

"Waffles don't know what day it is, Jake."

"He's got a point," [[Priya|Priya]] added. She had still not voted on the poll.

[[Mia|Mia]] was losing control. "THREE people need to vote on the form for it to be statistically valid."

"There are five of us," [[Jake|Jake]] said. "Nothing about this is statistical."

By 8 PM, the situation was dire. Nobody had eaten. The poll had exactly one response (Mia's own). [[Nate|Nate]] had sent three more voice notes, each suggesting a different restaurant, each with a detailed pros-and-cons analysis that belonged in a TED talk.

At 8:22 PM, [[Priya|Priya]] sent: "Guys I just ate cereal I'm out."

"PRIYA," [[Mia|Mia]] typed.

"I was hungry!! You all took too long!!"

[[Jake|Jake]] screenshotted the entire conversation and pinned it with the caption "Exhibit A: Why we can never have dinner."

At 8:45 PM, [[Tyler|Tyler]] sent a photo of himself at Waffle House. Alone. With a waffle. Captioned: "Leadership is a lonely road."

[[Mia|Mia]], [[Jake|Jake]], and [[Nate|Nate]] ended up at the Thai place at 9:15 PM. Nate's heartburn returned. He documented it in real time via voice notes that nobody listened to. [[Mia|Mia]] vowed to never plan dinner again. She would plan dinner again the following Thursday.`,
  },
  {
    story_id: "sample-group-012",
    chapter_index: 12,
    title: "The 2AM Philosophy Session",
    title_emoji: "\u{1F30C}",
    date: "August 20, 2024",
    time_range: "1:47 AM - 4:12 AM",
    participants: ["Jake", "Mia", "Tyler", "Priya", "Nate"],
    tags: ["Late Night", "Deep Thoughts", "Sleep Deprived"],
    narrative: `Nobody knows who starts the late-night spirals. They just happen. One moment the chat is dead, and the next someone sends something at an hour when no reasonable person should be awake, and suddenly everyone is.

This time it was [[Tyler|Tyler]], at 1:47 AM on a Tuesday: "Do you think dogs know they're dogs?"

For nine minutes, nothing. Then [[Jake|Jake]]: "Why are you like this."

"I'm serious," [[Tyler|Tyler]] continued. "Like does a golden retriever look in a mirror and think 'ah yes, I am a dog' or does it just think 'that guy looks friendly.'"

"It's 1:56 AM, Tyler."

"And yet you're responding."

[[Priya|Priya]] appeared: "WAIT this is actually a good question." She had not been online in hours. The notification somehow reached her.

[[Nate|Nate]] sent a voice note. Two minutes and thirty-one seconds. At 2 AM. He argued, with complete sincerity and surprising eloquence, that dogs have a sense of self but not a concept of species, and that the mirror test is flawed because dogs rely more on smell.

"I'm not listening to a two-minute voice note about dog consciousness at 2 AM," [[Jake|Jake]] wrote.

"Your loss," [[Nate|Nate]] typed — actually typed, for once. This was how everyone knew it was serious.

[[Mia|Mia]] joined at 2:15 AM: "I have a meeting at 8 AM. Why am I awake. Why are you all like this."

"Because Tyler asked about dogs," [[Priya|Priya]] explained.

"Of course he did."

Despite her protests, [[Mia|Mia]] immediately engaged: "Okay but here's the real question — if you could talk to your dog for five minutes and it could understand you, what would you say?"

"I'd apologize for every time I pretended to throw the ball," [[Jake|Jake]] said instantly.

"Dark," [[Tyler|Tyler]] replied.

"I'd explain that the vet is actually trying to help," [[Priya|Priya]] offered.

[[Nate|Nate]] sent another voice note — one minute forty-four seconds — in which he described, in emotional detail, a full conversation he'd want to have with his childhood dog, Biscuit. By the end of it, his voice was genuinely cracking.

"Nate, are you crying?" [[Mia|Mia]] asked.

"I MISS BISCUIT," [[Nate|Nate]] typed. Second typed message of the night. Unprecedented.

"We all miss Biscuit," [[Jake|Jake]] confirmed, despite none of them having ever met Biscuit.

The conversation drifted, as late-night chats do. They debated whether fish feel pain ([[Tyler|Tyler]]: "Define pain." [[Mia|Mia]]: "I'm going to cause you pain if you keep this up."). They discussed whether the universe is a simulation ([[Jake|Jake]] cited a 2019 message where Tyler said simulations were "dumb" — receipts, as always). [[Priya|Priya]] asked "do you think we'll still have this group chat in ten years" and everyone went quiet for a while.

"Yes," [[Mia|Mia]] said finally. "Someone will still have to plan dinner."

"And nobody will RSVP," [[Jake|Jake]] added.

"And I'll still be sending voice notes," [[Nate|Nate]] typed. Third typed message. Historic night.

"And I'll still be here with the good questions," [[Tyler|Tyler]] closed.

At 4:12 AM, [[Priya|Priya]] sent the last message: "Goodnight you weirdos. Same time next week?"

Nobody responded. They were already asleep. But yes — same time next week. Always.`,
  },
];
